/* eslint-disable no-unused-vars */
import React, { useContext, useEffect } from 'react'
import { useLazyQuery, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import moment from 'moment'

import ChartLine from '../../../../components/charts/ChartLine'
import ChartLineSkeleton from '../../../../components/skeletons/ChartLineSkeleton'
import { ChartLineThemes } from '../../../../components/charts/types/ChartLine'
import { formatCurrency, formatNumber } from '../../../../utils/Helpers'
import { GET_MULTIPLE_SHOPPING_CART_HISTORY } from '../../../../queries/multipleShoppingCarts'
import { DailyMonitoringDetailsFiltersContext } from '../../../../context/DailyMonitoringDetailsFiltersContext'
import { MultipleShoppingCartStatus, MultipleShoppingCartHistory } from '../../../../types/MultipleShoppingCart'

enum SeriesNames {
  gmv = 'GMV',
  averageTicket = 'Ticket médio',
  quantity = 'Quantidade (cesta)'
}

type Series = {
  name: string
  data: number[]
}

const MultipleShoppingSalesHistory = (): JSX.Element => {
  const { filters } = useContext(DailyMonitoringDetailsFiltersContext)
  const categories: Array<string> = []

  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_MULTIPLE_SHOPPING_CART_HISTORY, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    getData({
      variables: {
        filters: {
          createdAt: {
            initialDate: `${filters.date.year}-${filters.date.month}||/M`,
            finalDate: `${filters.date.year}-${filters.date.month}||/M`,
            format: 'yyyy-M',
            time_zone: filters.date.time_zone
          }
        }
      }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <ChartLineSkeleton />
  if (loading || !data || error) return <ChartLineSkeleton />
  if (!data.ordersAggs.aggregations.statuses.buckets) return <p>No data found</p>

  const bucketsStatus: MultipleShoppingCartStatus[] = data.ordersAggs.aggregations.statuses.buckets

  const seriesDeliveredGMV: Series = {
    name: SeriesNames.gmv,
    data: []
  }

  const seriesDeliveredTicketAverage: Series = {
    name: SeriesNames.averageTicket,
    data: []
  }

  const seriesDeliveredQuantity: Series = {
    name: SeriesNames.quantity,
    data: []
  }

  const buckets: MultipleShoppingCartHistory[] = bucketsStatus.filter((obj: MultipleShoppingCartStatus) => obj.key === 'DELIVERED')[0].history.buckets

  buckets.map((bucket: MultipleShoppingCartHistory) => {
    categories.push(bucket.key_as_string)

    seriesDeliveredQuantity.data.push(bucket.doc_count)
    seriesDeliveredTicketAverage.data.push(bucket.average.value)
    seriesDeliveredGMV.data.push(bucket.gmv.value)
  })

  const options = {
    chart: {
      id: 'sales-multiple-shopping-cart-history'
    },
    xaxis: {
      categories,
      type: 'datetime',
      labels: {
        formatter: (value: Date) => {
          return moment(value).format('DD-MM-YYYY')
        }
      }
    },
    yaxis: {
      categories,
      logarithmic: true,
      labels: {
        formatter: (value: number) => {
          return formatCurrency(value)
        }
      }
    },
    tooltip: {
      enabled: true,
      y: [
        { formatter: (value: number) => formatCurrency(value, 2) },
        { formatter: (value: number) => formatCurrency(value, 2) },
        { formatter: (value: number) => formatNumber(value) }
      ]
    }
  }

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>
      Cestas simultâneas por dia
    </Typography>
    <ChartLine options={options} series={[seriesDeliveredGMV, seriesDeliveredTicketAverage, seriesDeliveredQuantity]} theme={ChartLineThemes.multi} height={320} type="area" />
  </Paper>
}

export default MultipleShoppingSalesHistory
